import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  axiosGet,
  axiosPost,
  axiosInstance,
} from "../../services/axiosService";
import { TabTitle } from "../../utils/TitleFuntion";
import { useDispatch, useSelector } from "react-redux";
import {
  userSlice,
  setTextToWorkflowPrompt,
  setPrebuiltWorkflow,
} from "../../redux/User";
import { PREBUILT_WORKFLOWS } from "@/constants/prebuiltWorkflows";

// ─── DESIGN TOKENS (Airbnb/Dropbox inspired) ────────────────────────────────
const T = {
  font: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  coral: "#FB5F66",
  coralDark: "#e3232c",
  dark: "#1a1a2e",
  text: "#222",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
  bg: "#fafafa",
  card: "#fff",
  border: "#eef0f4",
  borderHover: "#d1d5db",
  radius: 16,
  radiusSm: 10,
  shadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
  shadowHover: "0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
  shadowLg: "0 20px 60px rgba(0,0,0,0.1)",
};

const CATEGORY_META: Record<
  string,
  { label: string; icon: string; color: string; gradient: string }
> = {
  "ai-automation": {
    label: "AI & Automation",
    icon: "🤖",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  ai: {
    label: "AI",
    icon: "🧠",
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  "sales-crm": {
    label: "Sales & CRM",
    icon: "🎯",
    color: "#2563eb",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  sales: {
    label: "Sales",
    icon: "💼",
    color: "#2563eb",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  marketing: {
    label: "Marketing",
    icon: "📣",
    color: "#db2777",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  ecommerce: {
    label: "E-Commerce",
    icon: "🛒",
    color: "#ea580c",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  },
  support: {
    label: "Support",
    icon: "🎧",
    color: "#059669",
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  },
  communication: {
    label: "Communication",
    icon: "💬",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
  },
  operations: {
    label: "Operations",
    icon: "⚙️",
    color: "#475569",
    gradient: "linear-gradient(135deg, #537895 0%, #09203f 100%)",
  },
  hr: {
    label: "HR & People",
    icon: "👥",
    color: "#a855f7",
    gradient: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
  },
  finance: {
    label: "Finance",
    icon: "💰",
    color: "#0284c7",
    gradient: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)",
  },
  "it-devops": {
    label: "DevOps",
    icon: "🔧",
    color: "#0891b2",
    gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
  },
  "data-analytics": {
    label: "Analytics",
    icon: "📊",
    color: "#d97706",
    gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
  },
  "project-mgmt": {
    label: "Projects",
    icon: "📋",
    color: "#4f46e5",
    gradient: "linear-gradient(135deg, #5b86e5 0%, #36d1dc 100%)",
  },
  productivity: {
    label: "Productivity",
    icon: "⚡",
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  },
  email: {
    label: "Email",
    icon: "📧",
    color: "#e11d48",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  video: {
    label: "Video & Ads",
    icon: "🎬",
    color: "#dc2626",
    gradient: "linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)",
  },
  research: {
    label: "Research",
    icon: "🔍",
    color: "#0d9488",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  "industry-specific": {
    label: "Industry",
    icon: "🏢",
    color: "#64748b",
    gradient: "linear-gradient(135deg, #537895 0%, #09203f 100%)",
  },
  "security-compliance": {
    label: "Security",
    icon: "🔒",
    color: "#dc2626",
    gradient: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
  },
  documents: {
    label: "Documents",
    icon: "📄",
    color: "#0369a1",
    gradient: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)",
  },
  "webhook-connectors": {
    label: "Webhooks",
    icon: "🔗",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
  },
  "nonprofit-education": {
    label: "Education",
    icon: "🎓",
    color: "#16a34a",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  "supply-chain": {
    label: "Supply Chain",
    icon: "📦",
    color: "#b45309",
    gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
  },
};

const CATEGORY_ORDER = [
  "ai-automation",
  "ai",
  "sales-crm",
  "sales",
  "marketing",
  "ecommerce",
  "operations",
  "support",
  "communication",
  "email",
  "hr",
  "finance",
  "it-devops",
  "data-analytics",
  "video",
  "research",
  "project-mgmt",
  "productivity",
  "industry-specific",
  "security-compliance",
  "documents",
  "webhook-connectors",
  "supply-chain",
  "nonprofit-education",
];

// ─── NODE TYPE ICONS for personalized thumbnails ─────────────────────────────
const NODE_ICONS: Record<string, { icon: string; label: string }> = {
  campaign: { icon: "▶", label: "Start" },
  hitApi: { icon: "⚡", label: "API" },
  sendEmail: { icon: "✉", label: "Email" },
  sendSMS: { icon: "💬", label: "SMS" },
  condition: { icon: "◇", label: "If" },
  delayAction: { icon: "⏱", label: "Wait" },
  runCode: { icon: "{ }", label: "Code" },
  sendMessage: { icon: "💭", label: "Reply" },
  webhookTrigger: { icon: "🔗", label: "Webhook" },
  approvalGate: { icon: "✓", label: "Approve" },
  whatsApp: { icon: "📱", label: "WhatsApp" },
  pushNotification: { icon: "🔔", label: "Push" },
};

// Generate a personalized mini-flow diagram SVG for each template
function MiniFlowThumb({ name, category }: { name: string; category: string }) {
  const meta = CATEGORY_META[category] || {
    gradient: "linear-gradient(135deg,#667eea,#764ba2)",
    icon: "⚡",
  };
  // Derive pseudo-random node count from name hash
  const hash = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const nodeCount = 3 + (hash % 3); // 3-5 nodes
  const nodeTypes = [
    "campaign",
    "hitApi",
    "condition",
    "sendEmail",
    "sendMessage",
  ];

  return (
    <div
      style={{
        height: 120,
        background: meta.gradient,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "0 20px",
      }}
    >
      {/* Decorative grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.06,
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Mini node flow */}
      {Array.from({ length: nodeCount }, (_, i) => {
        const type = nodeTypes[(hash + i) % nodeTypes.length];
        const ni = NODE_ICONS[type] || { icon: "●", label: "" };
        return (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: 6, zIndex: 1 }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                border: "1px solid rgba(255,255,255,0.3)",
                color: "#fff",
              }}
            >
              {ni.icon}
            </div>
            {i < nodeCount - 1 && (
              <div
                style={{
                  width: 16,
                  height: 2,
                  background: "rgba(255,255,255,0.4)",
                  borderRadius: 1,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function Templates() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [premades, setPremades] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("category");
    return cat && CATEGORY_META[cat] ? cat : "all";
  });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingBot, setLoadingBot] = useState("");
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const CARDS_PER_ROW = 4;
  const reduxLoggedIn = useSelector((state: any) => state.user.isLoggedIn);
  const isLoggedIn =
    reduxLoggedIn || !!window.localStorage.getItem("omni-token");

  // Sync activeFilter with URL search params so category pages are linkable/shareable
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeFilter === "all") {
      url.searchParams.delete("category");
    } else {
      url.searchParams.set("category", activeFilter);
    }
    window.history.replaceState({}, "", url.pathname + url.search);
  }, [activeFilter]);

  // Build instant fallback from PREBUILT_WORKFLOWS constants (no API needed)
  const instantTemplates = useMemo(() => {
    return Object.entries(PREBUILT_WORKFLOWS)
      .map(([name, wf]) => {
        const cleanName = name.replace(/^OM\d+\s+/i, "");
        const displayName = cleanName;
        const urlSlug = cleanName
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, "-")
          .slice(0, 80);
        const lower = cleanName.toLowerCase();
        const desc = ((wf as any).summary || "").toLowerCase();
        const combined = lower + " " + desc;

        // Intelligently categorize based on template name and description
        let category = "ai-automation";
        if (
          combined.match(
            /\b(email|newsletter|inbox|mailchimp|drip|welcome series|outreach)\b/,
          )
        )
          category = "email";
        else if (
          combined.match(
            /\b(sales|lead|pipeline|deal|crm|hubspot|salesforce|prospect|qualification|scoring)\b/,
          )
        )
          category = "sales-crm";
        else if (
          combined.match(
            /\b(marketing|campaign|social media|facebook|instagram|twitter|tiktok|ad|seo|content calendar|brand)\b/,
          )
        )
          category = "marketing";
        else if (
          combined.match(
            /\b(ecommerce|e-commerce|shopify|woocommerce|cart|order|product|inventory|shipping|storefront)\b/,
          )
        )
          category = "ecommerce";
        else if (
          combined.match(
            /\b(support|ticket|helpdesk|zendesk|customer service|freshdesk|intercom|complaint|faq)\b/,
          )
        )
          category = "support";
        else if (
          combined.match(
            /\b(hr|hiring|recruit|onboard|employee|pto|leave|payroll|talent|resume|candidate|interview)\b/,
          )
        )
          category = "hr";
        else if (
          combined.match(
            /\b(finance|invoice|payment|billing|accounting|expense|budget|quickbooks|xero|revenue)\b/,
          )
        )
          category = "finance";
        else if (
          combined.match(
            /\b(devops|deploy|ci\/cd|github|gitlab|monitor|incident|infrastructure|server|kubernetes)\b/,
          )
        )
          category = "it-devops";
        else if (
          combined.match(
            /\b(analytics|report|dashboard|data|kpi|metric|insight|chart)\b/,
          )
        )
          category = "data-analytics";
        else if (
          combined.match(
            /\b(video|youtube|podcast|recording|riverside|descript|media|stream)\b/,
          )
        )
          category = "video";
        else if (
          combined.match(
            /\b(research|competitor|market|analysis|survey|scrape|crawl|intel)\b/,
          )
        )
          category = "research";
        else if (
          combined.match(
            /\b(project|task|sprint|kanban|trello|asana|jira|standup|milestone)\b/,
          )
        )
          category = "project-mgmt";
        else if (
          combined.match(
            /\b(slack|discord|notification|message|communication|team|alert)\b/,
          )
        )
          category = "communication";
        else if (
          combined.match(
            /\b(document|pdf|contract|proposal|sign|form|template|google doc)\b/,
          )
        )
          category = "documents";
        else if (
          combined.match(/\b(webhook|api|integration|connect|sync|zapier)\b/)
        )
          category = "webhook-connectors";
        else if (
          combined.match(
            /\b(operations|process|workflow|automat|schedule|batch)\b/,
          )
        )
          category = "operations";
        else if (
          combined.match(
            /\b(security|compliance|audit|gdpr|hipaa|access|permission)\b/,
          )
        )
          category = "security-compliance";
        else if (
          combined.match(
            /\b(healthcare|patient|clinic|medical|insurance|ehr|emr|health)\b/,
          )
        )
          category = "industry-specific";
        else if (
          combined.match(
            /\b(education|school|student|course|learn|training|nonprofit)\b/,
          )
        )
          category = "nonprofit-education";
        else if (
          combined.match(
            /\b(supply chain|logistics|warehouse|shipping|fulfillment)\b/,
          )
        )
          category = "supply-chain";
        else if (
          combined.match(
            /\b(productivity|focus|time|reminder|habit|organize)\b/,
          )
        )
          category = "productivity";

        // Skip entries with numeric or empty names (malformed entries)
        if (!name || /^\d+$/.test(name) || name.length < 3) return null;

        return {
          name: cleanName,
          slug: urlSlug,
          url: name, // Keep original key for PREBUILT_WORKFLOWS lookup
          category,
          type: "workflow",
          description: (wf as any).summary || "",
          image: "",
          tags: [],
          _isLocal: true,
        };
      })
      .filter(Boolean) as any[];
  }, []);

  const fetchTemplates = useCallback(() => {
    // Show ALL local templates INSTANTLY (no API needed)
    setPremades(instantTemplates);
    setLoading(false);

    // Then merge in DB templates that aren't already covered by local ones
    const apiBase = axiosInstance.defaults.baseURL || "http://localhost:3001";
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    fetch(`${apiBase}/templates/all?limit=5000`, { signal: controller.signal })
      .then((r) => {
        clearTimeout(timeout);
        return r.ok ? r.json() : Promise.reject(r.statusText);
      })
      .then((data: any) => {
        const dbItems = data?.templates || (Array.isArray(data) ? data : []);
        if (dbItems.length === 0) {
          // No DB templates — try seeding
          axiosPost("/templates/seed", {}).catch(() => {});
          return;
        }
        // Merge: local templates + DB-only templates (avoid duplicates by slug)
        const localSlugs = new Set(instantTemplates.map((t: any) => t.slug));
        const dbOnly = dbItems
          .filter((t: any) => {
            // Clean the DB slug for display (remove om### prefixes)
            const cleanSlug = (t.slug || t.url || "").replace(/^om\d+-/, "");
            return !localSlugs.has(cleanSlug) && !localSlugs.has(t.slug);
          })
          .map((t: any) => ({
            ...t,
            // Clean up om-prefixed slugs
            slug: (t.slug || t.url || "").replace(/^om\d+-/, ""),
          }));
        if (dbOnly.length > 0) {
          setPremades((prev) => [...prev, ...dbOnly]);
        }
      })
      .catch(() => {
        clearTimeout(timeout);
        // API failed — keep showing local templates (already displayed)
        if (premades.length === 0) {
          setPremades(instantTemplates);
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.title = `${premades.length > 0 ? premades.length.toLocaleString() + "+" : "1,500+"} Workflow Templates — AI Automation | OmniEngage`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      `Browse ${premades.length > 0 ? premades.length.toLocaleString() + "+" : "1,500+"} ready-to-use workflow automation templates. AI-powered, fully customizable. Categories: Sales, Marketing, HR, Finance, DevOps, Support, and more. Start free.`,
    );
    fetchTemplates();
  }, [fetchTemplates]);

  // Auto-use template if ?use= query param is present (one-shot)
  const [autoUseHandled, setAutoUseHandled] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const useSlug = params.get("use");
    if (useSlug && !loadingBot && !autoUseHandled) {
      setAutoUseHandled(true);
      // Remove the ?use= param so back navigation doesn't re-trigger
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("use");
      window.history.replaceState({}, "", cleaned.pathname + cleaned.search);
      handleUseTemplate(useSlug);
    }
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(
      new Set(premades.map((p: any) => p.category || p.type)),
    ).filter(Boolean) as string[];
    return cats.sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a),
        bi = CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [premades]);

  // Semantic synonym map — maps user intent words to template keywords
  // Comprehensive synonym/intent map — maps natural language queries to search terms
  const SYNONYMS: Record<string, string[]> = useMemo(
    () => ({
      qualify: [
        "lead",
        "scoring",
        "qualification",
        "route",
        "triage",
        "classification",
        "mql",
        "sql",
      ],
      "cold email": [
        "outreach",
        "outbound",
        "sdr",
        "sequence",
        "prospecting",
        "cold",
        "icebreaker",
      ],
      "follow up": [
        "follow-up",
        "nurture",
        "drip",
        "sequence",
        "reminder",
        "check in",
      ],
      onboard: [
        "onboarding",
        "welcome",
        "new user",
        "activation",
        "signup",
        "getting started",
      ],
      churn: [
        "retention",
        "save",
        "win-back",
        "re-engagement",
        "frustration",
        "cancel",
        "downgrade",
      ],
      enrich: [
        "enrichment",
        "research",
        "find email",
        "find phone",
        "lookup",
        "validate",
        "append",
      ],
      notify: [
        "alert",
        "slack",
        "notification",
        "digest",
        "report",
        "update",
        "ping",
      ],
      crm: [
        "hubspot",
        "salesforce",
        "pipedrive",
        "marketo",
        "deal",
        "contact",
        "sync",
        "zoho",
      ],
      seo: [
        "search",
        "keyword",
        "content",
        "blog",
        "serp",
        "ranking",
        "organic",
        "backlink",
      ],
      ads: [
        "paid",
        "google ads",
        "meta",
        "facebook",
        "campaign",
        "ppc",
        "creative",
        "roas",
        "ad spend",
      ],
      social: [
        "linkedin",
        "twitter",
        "instagram",
        "tiktok",
        "post",
        "schedule",
        "social media",
        "buffer",
      ],
      competitor: [
        "competition",
        "alternative",
        "vs",
        "monitor",
        "intelligence",
        "spy",
        "benchmark",
      ],
      email: [
        "newsletter",
        "drip",
        "sequence",
        "sendgrid",
        "smtp",
        "deliverability",
        "mailchimp",
        "inbox",
      ],
      video: [
        "ugc",
        "tiktok",
        "kling",
        "creative",
        "hook",
        "reels",
        "youtube",
        "podcast",
        "riverside",
      ],
      ecommerce: [
        "shopify",
        "stripe",
        "order",
        "cart",
        "product",
        "inventory",
        "store",
        "woocommerce",
      ],
      support: [
        "ticket",
        "helpdesk",
        "customer service",
        "intercom",
        "zendesk",
        "freshdesk",
        "complaint",
      ],
      hr: [
        "hiring",
        "recruit",
        "employee",
        "pto",
        "leave",
        "payroll",
        "resume",
        "candidate",
        "interview",
        "talent",
      ],
      finance: [
        "invoice",
        "payment",
        "billing",
        "expense",
        "budget",
        "accounting",
        "quickbooks",
        "xero",
      ],
      report: [
        "analytics",
        "dashboard",
        "kpi",
        "metric",
        "insight",
        "summary",
        "weekly",
        "monthly",
      ],
      automate: [
        "workflow",
        "automation",
        "trigger",
        "process",
        "pipeline",
        "bot",
        "automatically",
      ],
      healthcare: [
        "patient",
        "medical",
        "clinic",
        "ehr",
        "emr",
        "eclinicalworks",
        "epic",
        "hipaa",
        "insurance",
      ],
      approval: [
        "approve",
        "review",
        "sign off",
        "gate",
        "human in the loop",
        "manager approval",
      ],
      data: [
        "sync",
        "transfer",
        "migrate",
        "import",
        "export",
        "etl",
        "integration",
        "api",
      ],
      ai: [
        "gpt",
        "openai",
        "claude",
        "generate",
        "summarize",
        "classify",
        "extract",
        "analyze",
      ],
      slack: ["channel", "message", "notification", "team", "workspace", "bot"],
      google: ["sheets", "drive", "calendar", "gmail", "docs", "workspace"],
      project: [
        "task",
        "sprint",
        "kanban",
        "trello",
        "asana",
        "jira",
        "standup",
        "milestone",
      ],
    }),
    [],
  );

  // Natural language intent phrases — map conversational queries to search terms
  const INTENT_PHRASES: Record<string, string[]> = useMemo(
    () => ({
      "i want to": [],
      "how do i": [],
      "can you": [],
      "help me": [],
      "when someone": ["trigger", "webhook", "event"],
      "new customer": ["onboard", "welcome", "signup", "new user"],
      "save time": ["automate", "workflow", "automation"],
      "send email when": ["email", "trigger", "notification"],
      track: ["monitor", "analytics", "report", "dashboard"],
      "every week": ["weekly", "schedule", "recurring", "digest"],
      "every month": ["monthly", "schedule", "recurring", "report"],
      "abandoned cart": ["cart", "recovery", "ecommerce", "abandoned"],
      "new lead": ["lead", "qualification", "crm", "inbound"],
      "customer complaint": ["support", "ticket", "escalation", "complaint"],
      "social post": ["social", "schedule", "post", "publish"],
      "data from": ["sync", "import", "api", "integration", "pull"],
      connect: ["integration", "sync", "api", "webhook"],
    }),
    [],
  );

  const filtered = useMemo(() => {
    if (!search.trim() && activeFilter === "all") return premades;

    return premades.filter((p: any) => {
      const cat = p.category || p.type;
      const matchCat = activeFilter === "all" || cat === activeFilter;
      if (!search.trim()) return matchCat;
      const q = search.toLowerCase().trim();
      const haystack = (
        (p.name || "") +
        " " +
        (p.description || "") +
        " " +
        (p.category || "") +
        " " +
        (p.tags || []).join(" ")
      ).toLowerCase();

      // Direct match
      if (haystack.includes(q)) return true; // Ignore category filter when searching

      // Multi-word: all words must match (skip stop words)
      const stopWords = new Set([
        "i",
        "a",
        "an",
        "the",
        "to",
        "for",
        "of",
        "in",
        "on",
        "my",
        "me",
        "do",
        "can",
        "you",
        "want",
        "need",
        "help",
        "how",
        "what",
        "is",
        "it",
        "with",
        "and",
        "or",
        "that",
        "this",
      ]);
      const words = q
        .split(/\s+/)
        .filter((w) => w.length > 1 && !stopWords.has(w));
      if (words.length > 1 && words.every((w) => haystack.includes(w)))
        return true;
      // Partial: at least half the words match
      if (
        words.length > 2 &&
        words.filter((w) => haystack.includes(w)).length >=
          Math.ceil(words.length * 0.6)
      )
        return true;

      // Synonym expansion: if query matches a synonym key, also search its expansions
      for (const [intent, synonyms] of Object.entries(SYNONYMS)) {
        if (q.includes(intent) || synonyms.some((s) => q.includes(s))) {
          const allTerms = [intent, ...synonyms];
          if (allTerms.some((t) => haystack.includes(t))) return true;
        }
      }

      // Natural language intent: strip common preambles and match on the core intent
      for (const [phrase, extraTerms] of Object.entries(INTENT_PHRASES)) {
        if (q.startsWith(phrase)) {
          const remainder = q.slice(phrase.length).trim();
          const searchTerms = [
            ...remainder
              .split(/\s+/)
              .filter((w) => w.length > 1 && !stopWords.has(w)),
            ...extraTerms,
          ];
          if (searchTerms.some((t) => haystack.includes(t))) return true;
        }
      }

      // Fuzzy: individual meaningful words match (at least one)
      if (
        words.length >= 1 &&
        words.some((w) => w.length >= 3 && haystack.includes(w))
      )
        return true;

      return false;
    });
  }, [premades, activeFilter, search, SYNONYMS, INTENT_PHRASES]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const p of filtered) {
      const cat = p.category || p.type || "other";
      if (!g[cat]) g[cat] = [];
      g[cat].push(p);
    }
    return g;
  }, [filtered]);

  const handleUseTemplate = useCallback(
    (botURL: string, templateName?: string, templateDesc?: string) => {
      const name = templateName || botURL;
      const promptText = templateDesc ? `${name}: ${templateDesc}` : name;

      // If not logged in, redirect to login with the prompt preserved
      if (!isLoggedIn) {
        dispatch(setTextToWorkflowPrompt(promptText));
        navigate("/login", {
          state: {
            prevPath: `/text-to-workflow?prompt=${encodeURIComponent(promptText)}`,
          },
        });
        return;
      }

      // Check if there's a prebuilt workflow for instant loading
      const prebuilt = PREBUILT_WORKFLOWS[name];
      if (prebuilt) {
        dispatch(setPrebuiltWorkflow(prebuilt));
        dispatch(setTextToWorkflowPrompt(name));
      } else {
        dispatch(setPrebuiltWorkflow(null));
        dispatch(setTextToWorkflowPrompt(promptText));
      }

      // Navigate immediately — no API call blocking the transition
      navigate("/text-to-workflow");
    },
    [navigate, dispatch],
  );

  const toggleCat = (cat: string) =>
    setExpandedCats((prev) => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.font }}>
      {/* ════════ TOP NAV — matches omniengage.co ════════ */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #eee",
          padding: "0 24px",
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 60,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <a href="https://omniengage.co" style={{ flexShrink: 0 }}>
            <img
              src="https://uploads-ssl.webflow.com/62b4493f97470447b1ad6715/62b44e97458c5493d213e1c2_logo.png"
              alt="OmniEngage"
              style={{ height: 36 }}
            />
          </a>
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              fontSize: 13,
              fontFamily: T.font,
            }}
          >
            <a
              href="/templates"
              style={{
                color: T.coral,
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              Templates
            </a>
            <a
              href={window.location.hostname === "localhost" ? "http://localhost:3003/#pricing_section" : "https://omniengage.co/#pricing_section"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#444", textDecoration: "none", fontWeight: 500 }}
            >
              Pricing
            </a>
            <a
              href={window.location.hostname === "localhost" ? "http://localhost:3003/blog" : "https://omniengage.co/blog"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#444", textDecoration: "none", fontWeight: 500 }}
            >
              Blog
            </a>
            <a
              href={window.location.hostname === "localhost" ? "http://localhost:3003/about" : "https://omniengage.co/about"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#444", textDecoration: "none", fontWeight: 500 }}
            >
              About
            </a>
          </nav>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {isLoggedIn ? (
              <button
                onClick={() => navigate("/dashboard")}
                style={{
                  padding: "7px 18px",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  background: T.coral,
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  style={{
                    padding: "7px 18px",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    background: "transparent",
                    border: "1px solid #ddd",
                    color: "#333",
                    cursor: "pointer",
                  }}
                >
                  Log in
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  style={{
                    padding: "7px 18px",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    background: T.coral,
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Sign up free
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ════════ SEARCH + FILTERS ════════ */}
      <div style={{ background: "#fff", borderBottom: "1px solid #eee" }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "40px 24px 24px",
          }}
        >
          {/* Title + subtitle */}
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: T.text,
              margin: "0 0 6px",
              letterSpacing: -0.5,
            }}
          >
            Workflow Templates
          </h1>
          <p
            style={{ fontSize: 15, color: T.textSecondary, margin: "0 0 24px" }}
          >
            {premades.length > 0 ? premades.length.toLocaleString() + "+" : "1,500+"} ready-to-use automations
          </p>

          {/* Search bar — full width, prominent */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#f8f9fb",
              borderRadius: 12,
              padding: "4px 6px 4px 18px",
              border: "1.5px solid #e5e7eb",
              transition: "all 0.2s",
              marginBottom: 20,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = T.coral;
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.boxShadow = `0 0 0 3px rgba(251,95,102,0.08)`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.background = "#f8f9fb";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <path
                d="M17.5 17.5L12.5 12.5M14.167 8.333A5.833 5.833 0 112.5 8.333a5.833 5.833 0 0111.667 0z"
                stroke="#9ca3af"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, category, or describe what you need..."
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 15,
                padding: "12px 8px",
                background: "transparent",
                color: T.text,
                fontFamily: T.font,
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  searchRef.current?.focus();
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  color: "#aaa",
                  padding: "0 6px",
                }}
              >
                ×
              </button>
            )}
            <button
              onClick={() => searchRef.current?.focus()}
              style={{
                padding: "10px 24px",
                borderRadius: 9,
                border: "none",
                cursor: "pointer",
                background: T.coral,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Search
            </button>
          </div>

          {/* Quick filters as chips */}
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            {[
              "Cart Recovery",
              "Welcome Series",
              "Lead Scoring",
              "AI Research",
              "Onboarding",
            ].map((q) => (
              <button
                key={q}
                onClick={() =>
                  setSearch(search === q.toLowerCase() ? "" : q.toLowerCase())
                }
                style={{
                  padding: "5px 14px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  background:
                    search.toLowerCase() === q.toLowerCase()
                      ? T.coral
                      : "#f3f4f6",
                  color:
                    search.toLowerCase() === q.toLowerCase() ? "#fff" : "#666",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Category pills */}
          <div
            style={{
              display: "flex",
              gap: 6,
              overflowX: "auto",
              paddingBottom: 4,
              scrollbarWidth: "none" as any,
            }}
          >
            <FilterPill
              active={activeFilter === "all"}
              onClick={() => setActiveFilter("all")}
              label={`All (${premades.length})`}
            />
            {categories.map((cat) => {
              const m = CATEGORY_META[cat];
              const count = premades.filter(
                (p) => (p.category || p.type) === cat,
              ).length;
              return (
                <FilterPill
                  key={cat}
                  active={activeFilter === cat}
                  onClick={() =>
                    setActiveFilter(activeFilter === cat ? "all" : cat)
                  }
                  label={`${m?.icon || "📁"} ${m?.label || cat} (${count})`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* ════════ CONTENT ════════ */}
      <main
        style={{ maxWidth: 1120, margin: "0 auto", padding: "32px 20px 80px" }}
      >
        {/* Result meta */}
        {search && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <span style={{ fontSize: 14, color: T.textSecondary }}>
              {filtered.length} result{filtered.length !== 1 ? "s" : ""} for{" "}
              <strong>"{search}"</strong>
            </span>
            {filtered.length === 0 && (
              <button
                onClick={() => {
                  dispatch(setTextToWorkflowPrompt(`Build: ${search}`));
                  navigate("/text-to-workflow");
                }}
                style={{
                  padding: "8px 20px",
                  borderRadius: T.radiusSm,
                  background: `linear-gradient(90deg, #667eea, #764ba2)`,
                  color: "#fff",
                  border: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Build "{search}" with AI →
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 80 }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: `3px solid ${T.border}`,
                borderTopColor: T.coral,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 12px",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <p style={{ color: T.textMuted, fontSize: 14 }}>{loadingMessage}</p>
          </div>
        ) : (
          <>
            {/* ── Category sections ── */}
            {(activeFilter === "all" ? categories : [activeFilter]).map(
              (cat) => {
                const items = grouped[cat];
                if (!items?.length) return null;
                const m = CATEGORY_META[cat] || {
                  label: cat,
                  icon: "📁",
                  color: "#6b7280",
                  gradient: "linear-gradient(135deg,#667eea,#764ba2)",
                };
                const isExpanded =
                  expandedCats.has(cat) || activeFilter !== "all";
                const visible = isExpanded
                  ? items
                  : items.slice(0, CARDS_PER_ROW);

                return (
                  <section key={cat} style={{ marginBottom: 48 }}>
                    {/* Section header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 16,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: m.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 18,
                          }}
                        >
                          {m.icon}
                        </div>
                        <div>
                          <h2
                            style={{
                              fontSize: 18,
                              fontWeight: 600,
                              color: T.text,
                              margin: 0,
                              lineHeight: 1.2,
                            }}
                          >
                            {m.label}
                          </h2>
                          <span style={{ fontSize: 12, color: T.textMuted }}>
                            {items.length} template
                            {items.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                      {items.length > CARDS_PER_ROW && (
                        <button
                          onClick={() => toggleCat(cat)}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 500,
                            color: T.coral,
                            background: "#FFF0F0",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          {isExpanded
                            ? "Show less"
                            : `See all ${items.length} →`}
                        </button>
                      )}
                    </div>

                    {/* Cards grid — single row by default, grid when expanded */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(auto-fill, minmax(250px, 1fr))`,
                        gap: 16,
                      }}
                    >
                      {visible.map((tpl: any, i: number) => (
                        <TemplateCard
                          key={tpl._id || tpl.slug || i}
                          template={tpl}
                          category={cat}
                          loading={false}
                          disabled={false}
                          onUse={() =>
                            handleUseTemplate(
                              tpl.url,
                              tpl.name || tpl.projectName,
                              tpl.description,
                            )
                          }
                          onViewDetails={() =>
                            navigate(`/templates/${tpl.slug || tpl.url}`)
                          }
                        />
                      ))}
                    </div>
                  </section>
                );
              },
            )}

            {/* ── Empty state ── */}
            {filtered.length === 0 && !search && (
              <div style={{ textAlign: "center", padding: 60 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                <div
                  style={{
                    color: T.textSecondary,
                    fontSize: 16,
                    fontWeight: 500,
                    marginBottom: 8,
                  }}
                >
                  No templates found
                </div>
                <div
                  style={{ color: T.textMuted, fontSize: 13, marginBottom: 20 }}
                >
                  Templates are loading or temporarily unavailable.
                </div>
                <div
                  style={{ display: "flex", gap: 12, justifyContent: "center" }}
                >
                  <button
                    onClick={fetchTemplates}
                    style={{
                      padding: "10px 24px",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      background: "#f3f4f6",
                      border: "none",
                      color: T.text,
                      cursor: "pointer",
                    }}
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => navigate("/text-to-workflow")}
                    style={{
                      padding: "10px 24px",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      background: T.coral,
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Build with AI instead →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ════════ CTA ════════ */}
        <div
          style={{
            background: T.dark,
            borderRadius: 20,
            padding: "48px 32px",
            textAlign: "center",
            marginTop: 20,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(251,95,102,0.2) 0%, transparent 70%)",
            }}
          />
          <h2
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "#fff",
              margin: "0 0 8px",
            }}
          >
            Don't see what you need?
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "#94a3b8",
              margin: "0 0 28px",
              maxWidth: 400,
              marginInline: "auto",
            }}
          >
            Describe your ideal automation in plain English. Our AI builds it in
            seconds.
          </p>
          <button
            onClick={() => navigate("/text-to-workflow")}
            style={{
              padding: "14px 36px",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              background: `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
              color: "#fff",
              border: "none",
              boxShadow: "0 4px 16px rgba(251,95,102,0.3)",
            }}
          >
            Build with AI →
          </button>
        </div>

        {/* Footer */}
        <footer
          style={{
            textAlign: "center",
            marginTop: 40,
            paddingBottom: 20,
            fontSize: 13,
            color: T.textMuted,
          }}
        >
          <a
            href="https://omniengage.co"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: T.coral, textDecoration: "none", fontWeight: 500 }}
          >
            omniengage.co
          </a>
          {" · "}
          <a
            href="https://omniengage.co/pricing"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: T.textMuted, textDecoration: "none" }}
          >
            Pricing
          </a>
          {" · "}
          <a
            href={window.location.hostname === "localhost" ? "http://localhost:3003/about" : "https://omniengage.co/about"}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: T.textMuted, textDecoration: "none" }}
          >
            About
          </a>
        </footer>
      </main>
    </div>
  );
}

// ─── TEMPLATE CARD ───────────────────────────────────────────────────────────
function TemplateCard({
  template,
  category,
  loading,
  disabled,
  onUse,
  onViewDetails,
}: {
  template: any;
  category: string;
  loading: boolean;
  disabled?: boolean;
  onUse: () => void;
  onViewDetails: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const name = template.name || "Workflow";
  // Strip markdown bold/formatting from description for clean display
  const rawDesc = template.description || "Pre-built automation workflow";
  const desc = rawDesc
    .replace(/\*\*/g, "")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .slice(0, 150);
  const isClickable = !loading && !disabled;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.card,
        borderRadius: T.radius,
        overflow: "hidden",
        border: `1px solid ${hovered && isClickable ? T.borderHover : T.border}`,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: hovered && isClickable ? "translateY(-4px)" : "none",
        boxShadow: hovered && isClickable ? T.shadowHover : T.shadow,
        display: "flex",
        flexDirection: "column",
        cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={isClickable ? onViewDetails : undefined}
    >
      {/* Personalized thumbnail with mini node flow */}
      <MiniFlowThumb name={name} category={category} />

      {/* Content */}
      <div
        style={{
          padding: "14px 16px 16px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: T.text,
            margin: "0 0 4px",
            lineHeight: 1.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical" as any,
          }}
        >
          {name}
        </h3>

        <p
          style={{
            fontSize: 12,
            color: T.textSecondary,
            margin: "0 0 14px",
            lineHeight: 1.55,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical" as any,
            flex: 1,
          }}
        >
          {desc}
        </p>

        {/* Tags */}
        {template.tags?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            {template.tags.slice(0, 3).map((tag: string, i: number) => (
              <span
                key={i}
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 6,
                  background: "#f3f4f6",
                  color: T.textSecondary,
                  fontWeight: 500,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
            style={{
              flex: 1,
              padding: "9px 0",
              borderRadius: T.radiusSm,
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              border: "1px solid #e5e7eb",
              transition: "all 0.2s",
              background: "#fff",
              color: T.textSecondary,
            }}
          >
            Details
          </button>
          <button
            disabled={!isClickable}
            onClick={(e) => {
              e.stopPropagation();
              onUse();
            }}
            style={{
              flex: 2,
              padding: "9px 0",
              borderRadius: T.radiusSm,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
              border: "none",
              transition: "all 0.2s",
              background:
                loading || disabled
                  ? "#f3f4f6"
                  : `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
              color: loading || disabled ? T.textMuted : "#fff",
            }}
          >
            {loading ? "Setting up..." : "Use Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FILTER PILL ─────────────────────────────────────────────────────────────
function FilterPill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 16px",
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
        border: "none",
        transition: "all 0.15s",
        background: active ? T.coral : "transparent",
        color: active ? "#fff" : T.textSecondary,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "#f3f4f6";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      {label}
    </button>
  );
}
